import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";

import { height, useZoomContext } from "../zoom-container";
import { VerticalGroup } from "../vertical-group";
import {
  contextPosition,
  devToolsPosition,
  effects,
  memoizationPosition,
  refsPosition,
  ssr,
  ssrPosition,
  state,
  statePosition,
  suspensePosition,
} from "../api-data";
import { Memoization, Refs } from "../refs-memoization";
import { SuspenseGroup } from "../suspense";
import { DevTools } from "../devtools";

import { CategoryTitle } from "./category-title";
import { ZoomForeignObject } from "./zoom-foreign-object";
import { Context } from "../context";
import {
  partHeight,
  elementWidth,
  hooksRectWidth,
  hooksRectHeight,
} from "../dimensions";
import PerfectArrow from "../perfect-arrow";
import { Button } from "../button";
import { Cross2Icon } from "@radix-ui/react-icons";
import { ExternalLink } from "../links";
import { useParams } from "react-router-dom";

const rectStroke = "hsl(24deg 5% 74%)";
const strokeLength = "8";

const strokeWidth = 2;
const divisionColor = "hsl(198.63deg 88.67% 48.43%)";

type HooksProps = {
  showDivisions: boolean;
  showLinks: boolean;
  scaleItems: boolean;
};

export function Hooks({ showDivisions, scaleItems, showLinks }: HooksProps) {
  const { zoomTo } = useZoomContext();

  const rectX = 400;
  const rectY = height * (1 / 4);

  const { part } = useParams();

  const [linkText, setLinkText] = React.useState(null);

  if (part && linkText) {
    setLinkText(null);
  }

  const zoomToInHooksGroup = (e, { x, y, k }) =>
    zoomTo(e, {
      x: rectX + x,
      y: rectY + y,
      k,
    });

  const lineHeight = 154;

  return (
    <g id="hooks" transform={`translate(${rectX},${rectY})`}>
      <CategoryTitle>Hooks</CategoryTitle>
      <rect
        name="shadow-suspense"
        fill="transparent"
        stroke="hsl(0deg 0% 100% / 90%)"
        rx={6}
        filter="url(#shadow2)"
        height={suspensePosition.height - 14}
        width={suspensePosition.width - 76}
        x={suspensePosition.x + 38}
        y={suspensePosition.y + 1}
      />
      <rect
        onClick={(e) =>
          zoomToInHooksGroup(e, {
            x: hooksRectWidth / 2,
            y: hooksRectHeight / 2,
            k: 2,
          })
        }
        x={0}
        y={0}
        rx={16}
        id="hooks"
        width={hooksRectWidth}
        height={hooksRectHeight}
        strokeWidth={strokeWidth}
        stroke={rectStroke}
        fill="hsl(218deg 15% 91%)"
        filter="url(#shadow)"
      />

      <ZoomForeignObject
        height={effects.size * partHeight}
        width={elementWidth}
        x={70}
        y={100}
        onZoom={zoomToInHooksGroup}
      >
        <VerticalGroup
          data={effects}
          name={"Effects"}
          scaleItems={scaleItems}
        />
      </ZoomForeignObject>

      <ZoomForeignObject
        height={statePosition.height}
        width={statePosition.width}
        x={statePosition.x}
        y={statePosition.y}
        onZoom={zoomToInHooksGroup}
      >
        <VerticalGroup data={state} name={"State"} scaleItems={scaleItems} />
      </ZoomForeignObject>

      <rect
        name="shadow-context"
        fill="transparent"
        stroke="hsl(0deg 0% 100% / 90%)"
        rx={6}
        filter="url(#shadow2)"
        height={contextPosition.height / 2 - 34}
        width={contextPosition.width - 26}
        x={contextPosition.x + 13}
        y={contextPosition.y + 1}
      />
      <ZoomForeignObject
        height={contextPosition.height}
        width={contextPosition.width}
        x={contextPosition.x}
        y={contextPosition.y}
        onZoom={zoomToInHooksGroup}
      >
        <Context scaleItems={scaleItems} />
      </ZoomForeignObject>

      <rect
        name="shadow-refs"
        fill="transparent"
        stroke="hsl(0deg 0% 100% / 90%)"
        rx={6}
        filter="url(#shadow2)"
        height={refsPosition.height - 2}
        width={refsPosition.width / 2 - 25}
        x={hooksRectWidth - 1}
        y={refsPosition.y + 1}
      />

      <ZoomForeignObject
        height={refsPosition.height}
        width={refsPosition.width}
        x={refsPosition.x}
        y={refsPosition.y}
        onZoom={zoomToInHooksGroup}
      >
        <Refs scaleItems={scaleItems} />
      </ZoomForeignObject>

      <line
        stroke={rectStroke}
        strokeDasharray={strokeLength}
        strokeWidth={strokeWidth}
        x1={hooksRectWidth}
        x2={hooksRectWidth}
        y1={15}
        y2={15 + lineHeight}
        strokeDashoffset={-4}
      />

      <rect
        name="shadow-memoization"
        fill="transparent"
        stroke="hsl(0deg 0% 100% / 90%)"
        rx={6}
        filter="url(#shadow2)"
        height={memoizationPosition.height - 2}
        width={memoizationPosition.width / 2 - 25}
        x={hooksRectWidth - 1}
        y={memoizationPosition.y + 1}
      />

      <ZoomForeignObject
        height={memoizationPosition.height}
        width={memoizationPosition.width}
        x={memoizationPosition.x}
        y={memoizationPosition.y}
        onZoom={zoomToInHooksGroup}
      >
        <Memoization scaleItems={scaleItems} />
      </ZoomForeignObject>

      <line
        stroke={rectStroke}
        strokeDasharray={strokeLength}
        strokeWidth={strokeWidth}
        x1={hooksRectWidth}
        x2={hooksRectWidth}
        y1={hooksRectHeight / 2.75}
        y2={hooksRectHeight / 2.75 + lineHeight}
        strokeDashoffset={-4}
      />

      <rect
        name="shadow-devtools"
        fill="transparent"
        stroke="hsl(0deg 0% 100% / 90%)"
        rx={6}
        filter="url(#shadow2)"
        height={devToolsPosition.height - 2}
        width={devToolsPosition.width / 2 - 25}
        x={hooksRectWidth - 1}
        y={devToolsPosition.y + 1}
      />

      <ZoomForeignObject
        height={devToolsPosition.height}
        width={devToolsPosition.width}
        x={devToolsPosition.x}
        y={devToolsPosition.y}
        onZoom={zoomToInHooksGroup}
      >
        <DevTools scaleItems={scaleItems} />
      </ZoomForeignObject>
      <line
        stroke={rectStroke}
        strokeDasharray={strokeLength}
        strokeWidth={strokeWidth}
        x1={hooksRectWidth}
        x2={hooksRectWidth}
        y1={hooksRectHeight - 220}
        y2={hooksRectHeight - 220 + lineHeight}
        strokeDashoffset={-4}
      />

      <ZoomForeignObject
        height={suspensePosition.height}
        width={suspensePosition.width}
        x={suspensePosition.x}
        y={suspensePosition.y}
        onZoom={zoomToInHooksGroup}
      >
        <SuspenseGroup scaleItems={scaleItems} />
      </ZoomForeignObject>

      <AnimatePresence>
        {showDivisions && (
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <g transform={`translate(5,${hooksRectHeight - 100})`}>
              <DivisionText transform="translate(5, -5)">
                Concurrent Rendering
              </DivisionText>
              <rect
                id="stabilizers-outline"
                width={elementWidth * 2 - 70}
                height={175}
                stroke={divisionColor}
                strokeWidth={3}
                fill="none"
                rx={5}
                strokeDasharray={3}
              />
            </g>
            <g transform={`translate(10,${hooksRectHeight - 70})`}>
              <DivisionText transform="translate(5, -5)">
                Transition
              </DivisionText>
              <rect
                id="stabilizers-outline"
                width={elementWidth - 45}
                height={140}
                stroke={divisionColor}
                strokeWidth={3}
                fill="none"
                rx={5}
                strokeDasharray={3}
              />
            </g>
            <g
              transform={`translate(${
                hooksRectWidth - elementWidth + 35
              },${90})`}
            >
              <DivisionText transform="rotate(270) translate(-200, -5)">
                Stabilizers
              </DivisionText>
              <rect
                id="stabilizers-outline"
                width={elementWidth - 40}
                height={330}
                stroke={divisionColor}
                strokeWidth={3}
                strokeDasharray={3}
                fill="none"
                rx={5}
              />
            </g>
            <g transform={`translate(95,${hooksRectHeight / 2 - 125})`}>
              <path
                d="M 0 0 q 120 0 245 0 Q 295 62 360 120 h 245 v 65 h -245 Q 321 84 245 65 h -245 v -65"
                stroke={divisionColor}
                strokeWidth={3}
                strokeDasharray={3}
                fill="none"
              />
              <DivisionText transform="translate(210, 110)">
                Library Hooks
              </DivisionText>
            </g>
          </motion.g>
        )}
      </AnimatePresence>

      <ZoomForeignObject
        height={ssrPosition.height}
        width={ssrPosition.width}
        x={ssrPosition.x}
        y={ssrPosition.y}
        onZoom={zoomToInHooksGroup}
      >
        <VerticalGroup
          data={ssr}
          name={"Server Rendering"}
          scaleItems={scaleItems}
        />
      </ZoomForeignObject>

      <DashRect rectHeight={hooksRectHeight} rectWidth={hooksRectWidth} />

      <AnimatePresence>
        {showLinks && (
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <UseMemoToUseRef setLinkText={setLinkText} />
            <TransitionArrow setLinkText={setLinkText} />
            <UseReducerToUseState setLinkText={setLinkText} />
            <UseMemoToUseCallback setLinkText={setLinkText} />
            <UseMemoMemoArrow setLinkText={setLinkText} />
            <ContextArrow setLinkText={setLinkText} />
            <ForwardRefImperativeHandleArrow setLinkText={setLinkText} />
            <UseRefForwardRef setLinkText={setLinkText} />
            <EffectsCallingOrder setLinkText={setLinkText} />
            <UseRefToUseState setLinkText={setLinkText} />
          </motion.g>
        )}
      </AnimatePresence>

      {showLinks && linkText && (
        <LinkText
          x={linkText.x}
          y={linkText.y}
          onClose={() => setLinkText(null)}
        >
          {linkText.el}
        </LinkText>
      )}
    </g>
  );
}

const makeSideOfDashes = (dashLength: number) => (length: number) =>
  Array.from({ length }).fill(dashLength).join(",");

function DashRect({ rectHeight, rectWidth }) {
  const makeDashes = makeSideOfDashes(Number(strokeLength));

  const nrOfDashesWide = rectWidth / Number(strokeLength);
  const wide = makeDashes(nrOfDashesWide - 2);

  const nrOfDashesHigh = rectHeight / Number(strokeLength);
  const high = makeDashes(nrOfDashesHigh);

  return (
    <rect
      x={0}
      y={0}
      rx={16}
      strokeDasharray={`${wide} ${rectHeight} ${wide} ${high}`}
      id="hooks-outline-dashed"
      width={rectWidth}
      height={rectHeight}
      strokeWidth={strokeWidth}
      stroke={rectStroke}
      fill="none"
      // ref={getPathLength}
    />
  );
}

const sketchyFont =
  "'Chalkboard SE', 'Segoe Print', 'Bradley Hand', Chilanka, TSCu_Comic, casual, cursive";

type DivisionTextProps = {
  children: string;
  transform: string;
};

function DivisionText({ children, transform }: DivisionTextProps) {
  const fontSize = 28;
  return (
    <>
      <text
        fill={"white"}
        stroke={"white"}
        fontFamily={sketchyFont}
        fontSize={fontSize}
        strokeWidth={3}
        aria-hidden
        transform={transform}
      >
        {children}
      </text>
      <text
        fill={divisionColor}
        fontFamily={sketchyFont}
        fontSize={fontSize}
        transform={transform}
      >
        {children}
      </text>
    </>
  );
}

function UseMemoToUseCallback({ setLinkText }) {
  const a = {
    x: hooksRectWidth - 245,
    y: hooksRectHeight / 1.91,
  };
  const b = {
    x: hooksRectWidth - 249,
    y: hooksRectHeight / 2.4,
  };
  return (
    <PerfectArrow
      a={a}
      b={b}
      flip
      bow={0.3}
      onClick={() => {
        setLinkText({
          x: b.x - 120,
          y: a.y,
          el: (
            <>
              <code>useCallback</code> can be viewed as a convenience wrapper
              around <code>useMemo</code> just for functions.{" "}
              <code>useCallback(fn, deps)</code> is equivalent to{" "}
              <code>{"useMemo(() => fn, deps)"}</code>.
            </>
          ),
        });
      }}
    />
  );
}

function UseRefToUseState({ setLinkText }) {
  const a = {
    x: hooksRectWidth - 180,
    y: 106,
  };

  const b = {
    x: hooksRectWidth / 2 + elementWidth / 2.8,
    y: hooksRectHeight / 2 - 130,
  };

  return (
    <g>
      <PerfectArrow
        a={a}
        b={b}
        bow={0.3}
        onClick={() => {
          setLinkText({
            x: b.x,
            y: b.y,
            el: (
              <>
                <code>useRef</code> is kind of like <code>useState</code>{" "}
                without a setter to update the UI. It’s useful for ’remembering’
                a value in a component without triggering rerenders. See{" "}
                <ExternalLink
                  className="text-black hover:text-stone-600"
                  href="https://beta.reactjs.org/learn/referencing-values-with-refs#how-does-use-ref-work-inside"
                >
                  How does useRef work inside?
                </ExternalLink>
                .
              </>
            ),
          });
        }}
      />
    </g>
  );
}

function UseReducerToUseState({ setLinkText }) {
  const a = {
    x: hooksRectWidth / 2 + elementWidth / 2.8,
    y: hooksRectHeight / 2 - 120,
  };

  const b = {
    x: hooksRectWidth / 2 + elementWidth / 2.75,
    y: hooksRectHeight / 2 - 40,
  };

  return (
    <g>
      <PerfectArrow
        a={a}
        b={b}
        flip
        bow={0.8}
        onClick={() => {
          setLinkText({
            x: b.x + 10,
            y: a.y,
            el: (
              <>
                <code>useState</code> can be viewed as a simpler version of{" "}
                <code>useReducer</code> (or vice-versa). Point is, they’re
                interchangeable, it’s mostly a matter of personal preference.
                Fun fact: React itself implements <code>useState</code> with{" "}
                <code>useReducer</code>.
              </>
            ),
          });
        }}
      />
    </g>
  );
}

function ForwardRefImperativeHandleArrow({ setLinkText }) {
  const b = {
    x: hooksRectWidth + 180,
    y: 110,
  };

  const a = {
    x: hooksRectWidth - 25,
    y: 53,
  };

  return (
    <PerfectArrow
      a={a}
      b={b}
      onClick={() => {
        setLinkText({
          x: a.x,
          y: b.y,
          el: (
            <>
              <p>
                <code>useImperativeHandle</code> is <em>always</em> used in
                combination with <code>forwardRef</code>.
              </p>
              <p>
                <code>useImperativeHandle</code> is basically a way for function
                components to expose <em>instance methods</em> even though they
                don't have instances.{" "}
                <small>
                  (
                  <ExternalLink
                    className="text-black hover:text-stone-600"
                    href="https://www.reddit.com/r/reactjs/comments/dfyclo/comment/f39w4tv/?context=3"
                  >
                    Thanks for this insight Mark!
                  </ExternalLink>
                  )
                </small>
              </p>
            </>
          ),
        });
      }}
    />
  );
}

function UseRefForwardRef({ setLinkText }) {
  const b = {
    x: hooksRectWidth - 140,
    y: 106,
  };
  const a = {
    x: hooksRectWidth + 120,
    y: 105,
  };
  return (
    <g>
      <PerfectArrow
        a={a}
        b={b}
        onClick={() => {
          setLinkText({
            x: b.x,
            y: b.y + 250,
            el: (
              <>
                <p>
                  <code>forwardRef</code> is a util for passing a ref object to
                  child component, it’s in the name right.{" "}
                  <small>
                    (It can also forward a{" "}
                    <a
                      className="underline"
                      href="https://julesblom.com/writing/ref-callback-use-cases"
                    >
                      ref callback
                    </a>{" "}
                    though that’s a rare use case.)
                  </small>
                </p>
                <p>
                  <code>forwardRef</code> is needed for{" "}
                  <ExternalLink
                    className="text-black hover:text-stone-600"
                    href="https://twitter.com/dan_abramov/status/1054960391955120128?lang=en"
                  >
                    legacy compatability
                  </ExternalLink>
                  . Hopefully a future version of React will let us forward a
                  ref simply by passing it with the <code>ref</code> prop.
                </p>
              </>
            ),
          });
        }}
      />
    </g>
  );
}

function ContextArrow({ setLinkText }) {
  const a = {
    x: hooksRectWidth / 2 + 230 / 2,
    y: 45,
  };
  const b = {
    x: hooksRectWidth / 2 + 220 / 2,
    y: -50,
  };
  return (
    <PerfectArrow
      a={a}
      b={b}
      startArrow
      bow={0.7}
      onClick={() => {
        setLinkText({
          x: b.x + 10,
          y: a.y + 50,
          el: <>These are duo. You can’t use one without the other.</>,
        });
      }}
    />
  );
}

function TransitionArrow({ setLinkText }) {
  const a = {
    x: 25,
    y: hooksRectHeight + 60,
  };
  const b = {
    x: 20,
    y: hooksRectHeight - 45,
  };
  return (
    <PerfectArrow
      a={a}
      b={b}
      flip
      bow={0.3}
      onClick={() => {
        setLinkText({
          x: b.x,
          y: b.y,
          el: (
            <>
              <code>startTransition</code> is also returned by{" "}
              <code>useTransition</code>, that’s more commonly used. The
              imported <code>startTransition</code> is meant to be used when{" "}
              <code>useTransition</code> is not available i.e. outside
              components.
            </>
          ),
        });
      }}
    />
  );
}

function UseMemoToUseRef({ setLinkText }) {
  const a = {
    x: 893 + 250 / 2,
    y: 300,
  };

  const b = {
    x: 890 + 250 / 2,
    y: 155,
  };

  return (
    <PerfectArrow
      a={a}
      b={b}
      onClick={() => {
        setLinkText({
          x: b.x,
          y: a.y + 10,
          el: (
            <>
              <code>useMemo</code> &amp; <code>useRef</code> both persist values
              in component. You can think of <code>useMemo</code> as{" "}
              <code>useRef</code> with auto-update capabilities.
              {/* <small>
                (hat tip to{" "}
                <a href="https://blog.thoughtspile.tech/2021/04/05/useref-usememo/">
                  Vladimir
                </a>{" "}
                for this insight.)
              </small> */}
            </>
          ),
        });
      }}
    />
  );
}

function UseMemoMemoArrow({ setLinkText }) {
  const a = {
    x: 893 + 285 / 2,
    y: 288,
  };

  const b = {
    x: 1285,
    y: 290,
  };

  return (
    <PerfectArrow
      a={a}
      b={b}
      startArrow
      // flip
      bow={0.15}
      onClick={() => {
        setLinkText({
          x: b.x + 10,
          y: a.y + 50,
          el: (
            <>
              `memo` and `useMemo` are similare. `useMemo` is for memoizing
              values in components using a dependency list. `memo` is for
              memoizing components, `memo` sort of turns your components props
              into a dependency list for that component.
            </>
          ),
        });
      }}
    />
  );
}

function EffectsCallingOrder({ setLinkText }) {
  const a = { x: 93, y: 300 };
  const b = { x: 93, y: 140 };
  return (
    <PerfectArrow
      a={a}
      b={b}
      clickPadding={30}
      onClick={() => {
        setLinkText({
          x: b.x - 260,
          y: a.y,
          el: (
            <>
              All effect hooks have the same function signature, the difference
              is <em>when</em> they run. <code>useEffect</code> runs after React
              paints the screen while <code>useLayoutEffect</code> runs before.
              Most of the time you’ll need <code>useEffect</code>. It’s safe to
              say you can ignore <code>useLayoutEffect</code>, it is really only
              meant for use in CSS-in-JS libraries.
            </>
          ),
        });
      }}
    />
  );
}

const ForeignObjectText = ({ children }) => (
  <div className="mx-2 my-2 grid h-full items-center">
    <div className="pointer-events-auto rounded border border-stone-200 bg-white/75 px-4 py-3 drop-shadow-sm backdrop-blur-sm">
      {children}
    </div>
  </div>
);

function LinkText({
  x,
  y,
  children,
  onClose,
}: React.PropsWithChildren<{ x: number; y: number; onClose(): void }>) {
  const width = 280;
  const height = 335;

  console.log({ x, y });

  return (
    <foreignObject
      height={height}
      width={width}
      x={x}
      y={y - 260}
      // className="pointer-events-none"
    >
      <ForeignObjectText>
        <Button
          className="absolute top-[-6px] right-[-6px] inline-flex h-[20px] w-[20px] appearance-none items-center justify-center rounded-full text-sm focus:shadow-[0_0_0_2px] focus:outline-none"
          aria-label="Close"
          onPress={onClose}
        >
          <Cross2Icon />
        </Button>
        {children}
      </ForeignObjectText>
    </foreignObject>
  );
}
